// activity-lines.js — the Overview's chat activity chart.
//
// A time chart: time along the bottom, actions per time slice up the side,
// one line per chat (busiest seven, the rest summed as "Other chats").
// Actions are every tool call a chat makes: edits, commands, file reads.
// Hover the chart for every chat's count at that moment; hover a legend row
// for what the chat is and did; click opens it on the Sessions page. Data:
// GET /v1/activity/chats?range= from the daemon; hosted,
// GET /v1/teams/activity/chats, which carries counts and an encrypted
// title only, so owners, files and repos come from the dashboard's own
// decrypted lease and heartbeat data.
import { api, AuthRedirectError } from "./api.js";
import { state } from "./state.js";
import { agentsState } from "./page-state.js";
import { escapeHTML, ago, parseUTC } from "./util.js";
import { chatLabel, friendlyResource } from "./format.js";
import { navigate } from "./router.js";
import { isCloudMode } from "./ui.js";
import { memberName, myMemberName, deviceLabelFor } from "./members.js";
import { resolveChatRepo } from "./chats.js";
import { decryptHint } from "./crypto.js";

const RANGES = [
  { key: "1h", label: "1h", tick: 10 * 60, slice: "per 2 min" },
  { key: "6h", label: "6h", tick: 3600, slice: "per 10 min" },
  { key: "1d", label: "1 day", tick: 3 * 3600, slice: "per 30 min" },
  { key: "7d", label: "7 days", tick: 86400, slice: "per 4 hours" },
  { key: "30d", label: "30 days", tick: 5 * 86400, slice: "per 12 hours" },
];
// Eight validated categorical slots (CSS --al-c1..8, light and dark). The
// busiest seven chats get a line each; the rest share "Other chats".
const SLOTS = 8;
const LINES = 7;
const REFRESH_MS = 30 * 1000;
const RANGE_KEY = "ac.activityRange";
const M = { l: 40, r: 14, t: 14, b: 28 };
// Full screen can chart one kind of action and group chats; the Overview
// panel always shows every action, one line per chat.
const KINDS = [
  { key: "all", label: "All actions", field: "series" },
  { key: "edits", label: "Edits", field: "edited" },
  { key: "commands", label: "Commands", field: "commanded" },
  { key: "reads", label: "Reads", field: "read" },
];
const GROUPS = [
  { key: "chat", label: "Chat", of: (c) => c.agent_id, name: (c) => chatName(c) },
  { key: "tool", label: "Tool", of: (c) => c.runtime || chatLabel(c.agent_id).split(" ")[0], name: (c) => c.runtime || chatLabel(c.agent_id).split(" ")[0] },
  { key: "repo", label: "Repo", of: (c) => baseName(c.repo) || "", name: (c) => baseName(c.repo) || "No repo" },
  { key: "person", label: "Person", of: (c) => c.owner || "", name: (c) => c.owner || "Unknown person" },
  { key: "device", label: "Device", of: (c) => c.device || "", name: (c) => c.device || "Unknown device" },
];
const PANEL_VIEW = { kind: "all", group: "chat", only: {}, q: "", hidden: new Set() };

const al = {
  range: readRange(),
  data: null,
  error: "",
  full: false,
  pinned: "",
  tipPinned: false,
  timer: null,
  inflight: false,
  hydrateTries: 0,
  failures: 0,
  slots: new Map(),
  geom: null,
  // The full-screen view: kind of action, grouping, chip filters
  // (dimension → set of kept values; empty keeps all), name search and
  // hidden legend entries.
  view: { kind: "all", group: "chat", only: { tool: new Set(), repo: new Set(), person: new Set(), device: new Set() }, q: "", hidden: new Set() },
};

function readRange() {
  try {
    const v = localStorage.getItem(RANGE_KEY);
    if (RANGES.some((r) => r.key === v)) return v;
  } catch (_) {
    /* storage blocked: start on 1h */
  }
  return "1h";
}

function saveRange(v) {
  try {
    localStorage.setItem(RANGE_KEY, v);
  } catch (_) {
    /* storage blocked: the choice lasts this visit */
  }
}

const rangeOf = (key) => RANGES.find((r) => r.key === key) || RANGES[0];
const canHover = () => window.matchMedia && window.matchMedia("(hover: hover)").matches;
const baseName = (p) => (p ? String(p).replace(/\/+$/, "").split("/").pop() : "");

// relPath shows a file relative to the chat's repo when it is inside it.
function relPath(p, repo) {
  const f = friendlyResource(p) || p || "";
  const root = String(repo || "").replace(/\/+$/, "");
  if (!root) return f;
  for (const r of [root, "/private" + root, root.replace(/^\/private/, "")]) {
    if (f.startsWith(r + "/")) return f.slice(r.length + 1);
  }
  return f;
}

// chatName: the chat's title in its tool (the name the person gave it, or
// the tool's own), else its repo, else its tool and short id.
function chatName(c) {
  return c.name || baseName(c.repo) || chatLabel(c.agent_id);
}

function shortID(agentID) {
  const rest = String(agentID).split("/").slice(1).join("/");
  return (rest || agentID).slice(0, 8);
}

async function load() {
  if (al.inflight) return;
  al.inflight = true;
  setBusy(true);
  const range = al.range;
  try {
    const cloud = isCloudMode();
    const d = await api((cloud ? "/v1/teams/activity/chats?range=" : "/v1/activity/chats?range=") + encodeURIComponent(range));
    if (range !== al.range) return;
    if (cloud) await describeHosted(d);
    al.data = d;
    al.error = "";
    al.hydrateTries = 0;
    al.failures = 0;
  } catch (err) {
    if (err instanceof AuthRedirectError) {
      // The sign-in is still loading (or the login screen is taking over):
      // keep the loading chart and try again once it has.
      if (err.message.endsWith("session_hydrating") && al.hydrateTries++ < 30) setTimeout(load, 700);
      return;
    }
    // A passing failure keeps the loading chart and retries (2s, 4s, 8s);
    // only one that persists is shown.
    if (!al.data && al.failures < 3) {
      setTimeout(load, 2000 << al.failures++);
      return;
    }
    al.error = (err && err.message) || String(err);
  } finally {
    al.inflight = false;
    setBusy(false);
  }
  render();
}

// describeHosted fills in what the daemon would have: title, owner, repo,
// last action and held files, and narrows to the viewer's own chats unless
// the Overview is on the team view.
async function describeHosted(d) {
  for (const c of d.chats) {
    const owner = memberName(c.machine_id);
    c.owner = owner && owner !== c.machine_id ? owner : "";
    c.runtime = String(c.agent_id).split("/")[0];
    c.repo = resolveChatRepo(c.agent_id);
    c.device = deviceLabelFor({ laptopID: c.machine_id });
    c.name = c.title_hint ? (await decryptHint(c.title_hint)) || "" : "";
    const hb = (state.heartbeats || []).find((h) => h.agent_id === c.agent_id);
    if (hb) {
      c.last_tool = hb.lastTool || hb.last_tool || "";
      c.last_file = hb.lastFile || "";
      const seen = parseUTC(hb.last_seen);
      c.last_seen = Number.isFinite(seen) ? Math.floor(seen / 1000) : 0;
    }
    c.held_files = (state.leases || []).filter((l) => l.AgentID === c.agent_id).map((l) => l.Resource || "");
    c.top_files = [];
  }
  if (state.overviewView !== "team") {
    const mine = myMemberName();
    if (mine) d.chats = d.chats.filter((c) => c.owner === mine);
  }
}

// assignSlots gives each charted chat a colour that stays with it while it
// stays charted: a chat never changes colour because another came or went.
function assignSlots(ids) {
  for (const id of [...al.slots.keys()]) if (!ids.includes(id)) al.slots.delete(id);
  const used = new Set(al.slots.values());
  for (const id of ids) {
    if (al.slots.has(id)) continue;
    let s = 1;
    while (used.has(s)) s++;
    al.slots.set(id, s);
    used.add(s);
  }
}

const groupOf = (key) => GROUPS.find((g) => g.key === key) || GROUPS[0];
const kindOf = (key) => KINDS.find((k) => k.key === key) || KINDS[0];

// visibleChats applies the full-screen chip filters and name search.
function visibleChats(d, view) {
  const q = view.q.trim().toLowerCase();
  return d.chats.filter((c) => {
    for (const dim of ["tool", "repo", "person", "device"]) {
      const keep = view.only[dim];
      if (keep && keep.size && !keep.has(groupOf(dim).of(c))) return false;
    }
    return !q || [chatName(c), c.agent_id, c.repo, c.owner, c.runtime].some((f) => String(f || "").toLowerCase().includes(q));
  });
}

// groups sums the chats into one entry per group (per chat when grouped
// by chat), busiest first, with the values of the chosen kind of action.
function groups(d, view) {
  const g = groupOf(view.group);
  const field = kindOf(view.kind).field;
  const by = new Map();
  for (const c of visibleChats(d, view)) {
    const key = view.group + ":" + g.of(c);
    let e = by.get(key);
    if (!e) {
      e = { key, name: g.name(c), chat: view.group === "chat" ? c : null, chats: 0, total: 0, values: new Array(c.series.length).fill(0) };
      by.set(key, e);
    }
    e.chats++;
    c[field].forEach((v, i) => {
      e.values[i] += v;
      e.total += v;
    });
  }
  return [...by.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

// series turns the response into chart lines: the busiest shown entries,
// then the rest summed as one "others" line.
function series(d, view = PANEL_VIEW) {
  const shown = groups(d, view).filter((e) => !view.hidden.has(e.key));
  const top = shown.slice(0, shown.length > SLOTS ? LINES : SLOTS);
  assignSlots(top.map((e) => e.key));
  const out = top.map((e) => ({ id: e.chat ? e.chat.agent_id : "", key: e.key, name: e.name, color: "var(--al-c" + al.slots.get(e.key) + ")", values: e.values, chat: e.chat, total: e.total, chats: e.chats }));
  const rest = shown.slice(top.length);
  if (rest.length) {
    const values = rest[0].values.map((_, i) => rest.reduce((s, e) => s + e.values[i], 0));
    const noun = view.group === "chat" ? "chats" : groupOf(view.group).label.toLowerCase() + "s";
    out.push({ id: "", key: "", name: rest.length + " other " + noun, color: "var(--al-other)", values, chat: null, total: rest.reduce((s, e) => s + e.total, 0), chats: 0 });
  }
  return out;
}

// niceMax rounds up to 1, 2 or 5 × 10^k and returns it with its tick step.
function niceTicks(max) {
  if (max <= 4) return { top: Math.max(1, max), step: 1 };
  const raw = max / 4;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].map((m) => m * p).find((s) => s >= raw);
  return { top: Math.ceil(max / step) * step, step };
}

// timeTicks returns the time labels for the x axis: round local times
// (every 10 min, hour, 3 hours, day or 5 days, by range).
function timeTicks(d, rg) {
  const out = [];
  const startMs = d.start * 1000;
  const endMs = d.end * 1000;
  const t = new Date(startMs);
  if (rg.tick >= 86400) {
    t.setHours(0, 0, 0, 0);
    t.setDate(t.getDate() + 1);
    while (t.getTime() < endMs) {
      if (rg.tick === 86400 || (t.getDate() - 1) % (rg.tick / 86400) === 0) {
        out.push({ ms: t.getTime(), label: t.toLocaleDateString([], { month: "short", day: "numeric" }) });
      }
      t.setDate(t.getDate() + 1);
    }
    return out;
  }
  const stepMin = rg.tick / 60;
  t.setSeconds(0, 0);
  t.setMinutes(Math.ceil((t.getHours() * 60 + t.getMinutes()) / stepMin) * stepMin - t.getHours() * 60);
  while (t.getTime() <= endMs) {
    const midnight = t.getHours() === 0 && t.getMinutes() === 0;
    const label = midnight
      ? t.toLocaleDateString([], { month: "short", day: "numeric" })
      : t.toLocaleTimeString([], { hour: "numeric", minute: t.getMinutes() ? "2-digit" : undefined });
    if (t.getTime() >= startMs) out.push({ ms: t.getTime(), label });
    t.setTime(t.getTime() + rg.tick * 1000);
  }
  return out;
}

function sliceLabel(d, i) {
  const from = new Date((d.start + i * d.bucket_seconds) * 1000);
  const to = new Date((d.start + (i + 1) * d.bucket_seconds) * 1000);
  const t = (x) => x.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const day = (x) => x.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  return d.bucket_seconds >= 3600 * 2 ? day(from) + " " + t(from) + " – " + t(to) : t(from) + " – " + t(to);
}

// Full screen on a wide window puts the legend in a column beside the
// chart, and the chart fills the height left under the toolbar.
const LEGEND_W = 340;
const splitView = () => al.full && window.innerWidth >= 1000;

function chartHeight() {
  if (!al.full) return 230;
  const bar = document.getElementById("activity-toolbar");
  const used = 130 + (bar && !bar.hidden ? bar.offsetHeight : 0) + (splitView() ? 0 : 260);
  return Math.max(300, window.innerHeight - used);
}

// frameSVG draws the axes and grid; every state (loading, empty, data)
// starts from it so switching between them never moves the layout.
function frameSVG(w, h, yTicks, xTicks, d) {
  const pw = w - M.l - M.r;
  const ph = h - M.t - M.b;
  let s = "";
  for (const v of yTicks.values) {
    const y = M.t + ph - (v / yTicks.top) * ph;
    s += '<line class="al-grid' + (v === 0 ? " al-base" : "") + '" x1="' + M.l + '" x2="' + (w - M.r) + '" y1="' + y.toFixed(1) + '" y2="' + y.toFixed(1) + '"/>';
    if (yTicks.labels) s += '<text class="al-ylabel" x="' + (M.l - 8) + '" y="' + (y + 3.5).toFixed(1) + '">' + v.toLocaleString() + "</text>";
  }
  if (d) {
    const span = (d.end - d.start) * 1000;
    let lastX = -Infinity;
    for (const t of xTicks) {
      const x = M.l + ((t.ms - d.start * 1000) / span) * pw;
      // Skip labels at the very edges and any that would touch the last one.
      if (x < M.l + 18 || x > w - M.r - 18 || x - lastX < 64) continue;
      lastX = x;
      s += '<line class="al-xtick" x1="' + x.toFixed(1) + '" x2="' + x.toFixed(1) + '" y1="' + (M.t + ph) + '" y2="' + (M.t + ph + 4) + '"/>';
      s += '<text class="al-xlabel" x="' + x.toFixed(1) + '" y="' + (h - 8) + '">' + escapeHTML(t.label) + "</text>";
    }
  }
  return s;
}

function chartHTML(d, lines, w, h, kind) {
  const n = lines.length ? lines[0].values.length : 0;
  let max = 0;
  for (const l of lines) for (const v of l.values) if (v > max) max = v;
  const { top, step } = niceTicks(max);
  const values = [];
  for (let v = 0; v <= top + 1e-9; v += step) values.push(Math.round(v));
  const pw = w - M.l - M.r;
  const ph = h - M.t - M.b;
  const x = (i) => M.l + ((i + 0.5) / n) * pw;
  const y = (v) => M.t + ph - (v / top) * ph;
  al.geom = { w, h, n, top, x, y, pw, lines };
  const paths = lines
    .map((l, k) => ({ l, k }))
    .reverse()
    .map(
      ({ l, k }) =>
        '<path class="al-series" data-k="' + k + '" style="stroke:' + l.color + '" d="' +
        l.values.map((v, i) => (i ? "L" : "M") + x(i).toFixed(1) + "," + y(v).toFixed(1)).join("") +
        '"/>',
    )
    .join("");
  const rg = rangeOf(d.range);
  return (
    '<div class="al-unit">' + escapeHTML((kind && kind.key !== "all" ? kind.label : "Actions") + " " + rg.slice) + "</div>" +
    '<svg class="al-svg" width="' + w + '" height="' + h + '" role="img" aria-label="' + escapeHTML("Chat activity over the last " + rg.label) + '">' +
    frameSVG(w, h, { values, top, labels: true }, timeTicks(d, rg), d) +
    paths +
    '<line class="al-cross" x1="0" x2="0" y1="' + M.t + '" y2="' + (M.t + ph) + '"/>' +
    '<g class="al-dots"></g>' +
    '<rect class="al-hit" x="' + M.l + '" y="' + M.t + '" width="' + pw + '" height="' + ph + '"/>' +
    "</svg>"
  );
}

function chatSub(c) {
  return [baseName(c.repo), c.owner, c.runtime || chatLabel(c.agent_id).split(" ")[0], shortID(c.agent_id)].filter(Boolean).join(" · ");
}

function legendHTML(lines) {
  const rows = lines.map((l, k) => {
    const c = l.chat;
    return (
      '<div class="al-row' + (c && al.pinned === c.agent_id ? " pinned" : "") + '" data-k="' + k + '"' +
      (c ? ' data-al-agent="' + escapeHTML(c.agent_id) + '" tabindex="0" role="button"' : "") +
      ' aria-label="' + escapeHTML(l.name + ": " + l.total + " actions") + '">' +
      '<span class="al-key" style="background:' + l.color + '"></span>' +
      '<span class="al-name"><span class="al-title">' + escapeHTML(l.name) + '</span><span class="al-sub">' + escapeHTML(c ? chatSub(c) : "summed into one line") + "</span></span>" +
      '<span class="al-total">' + l.total.toLocaleString() + "</span>" +
      "</div>"
    );
  });
  return '<div class="al-legend">' + rows.join("") + "</div>";
}

// fullLegendHTML lists every entry in full screen; clicking one shows or
// hides its line.
function fullLegendHTML(d, lines) {
  const all = groups(d, al.view);
  const charted = new Map(lines.filter((l) => l.key).map((l, k) => [l.key, { l, k }]));
  const rows = all.map((e) => {
    const hidden = al.view.hidden.has(e.key);
    const on = charted.get(e.key);
    const color = hidden ? "transparent" : on ? on.l.color : "var(--al-other)";
    const sub = e.chat ? chatSub(e.chat) : e.chats + (e.chats === 1 ? " chat" : " chats");
    return (
      '<div class="al-row al-toggle' + (hidden ? " off" : "") + '" data-al-key="' + escapeHTML(e.key) + '"' +
      (on ? ' data-k="' + on.k + '"' : "") + (e.chat ? ' data-al-agent="' + escapeHTML(e.chat.agent_id) + '"' : "") +
      ' tabindex="0" role="switch" aria-checked="' + !hidden + '" aria-label="' + escapeHTML((hidden ? "Show " : "Hide ") + e.name) + '">' +
      '<span class="al-key" style="background:' + color + '"></span>' +
      '<span class="al-name"><span class="al-title">' + escapeHTML(e.name) + '</span><span class="al-sub">' + escapeHTML(sub) + "</span></span>" +
      '<span class="al-total">' + e.total.toLocaleString() + "</span>" +
      (e.chat ? '<button type="button" class="al-open-btn" data-al-open="' + escapeHTML(e.chat.agent_id) + '" aria-label="Open this session" data-tooltip="Open this session">&#x2197;</button>' : "") +
      "</div>"
    );
  });
  const hiddenCount = all.filter((e) => al.view.hidden.has(e.key)).length;
  return (
    '<div class="al-legend-head"><span>' + all.length + " " + (al.view.group === "chat" ? "chats" : groupOf(al.view.group).label.toLowerCase() + "s") +
    (hiddenCount ? " · " + hiddenCount + " hidden" : "") + " · click one to show or hide its line</span>" +
    '<span><button type="button" class="btn btn-sm" data-al-all="show">Show all</button> <button type="button" class="btn btn-sm" data-al-all="hide">Hide all</button></span></div>' +
    '<div class="al-legend al-legend-full">' + (rows.join("") || '<div class="al-msg-detail">No chats match these filters.</div>') + "</div>"
  );
}

// toolbarHTML: kind of action, grouping, name search and chip filters.
// Person and device appear only when the chats have more than one.
function dimValues(d, dim) {
  const g = groupOf(dim);
  return [...new Set(d.chats.map((c) => g.of(c)))].filter(Boolean).sort();
}

function toolbarHTML(d) {
  const seg = (attr, items, cur) =>
    '<div class="segmented-control" role="group">' +
    items.map((i) => '<button type="button" class="segmented-button' + (i.key === cur ? " active" : "") + '" ' + attr + '="' + i.key + '" aria-pressed="' + (i.key === cur) + '">' + escapeHTML(i.label) + "</button>").join("") +
    "</div>";
  const dims = ["tool", "repo", "person", "device"].filter((dim) => dimValues(d, dim).length > 1);
  const groupItems = GROUPS.filter((g) => g.key === "chat" || dims.includes(g.key));
  const chips = dims
    .map((dim) => {
      const keep = al.view.only[dim];
      return (
        '<div class="al-chip-row"><span class="al-tool-label">' + groupOf(dim).label + "</span>" +
        dimValues(d, dim)
          .map((v) => '<button type="button" class="al-chip' + (keep.has(v) ? " on" : "") + '" data-al-chip="' + dim + '" data-v="' + escapeHTML(v) + '" aria-pressed="' + keep.has(v) + '">' + escapeHTML(v) + "</button>")
          .join("") +
        (keep.size ? '<button type="button" class="al-chip-clear" data-al-chip-clear="' + dim + '">Any</button>' : "") +
        "</div>"
      );
    })
    .join("");
  return (
    '<div class="al-tool-row">' +
    '<span class="al-tool"><span class="al-tool-label">Group by</span>' + seg("data-al-group", groupItems, al.view.group) + "</span>" +
    '<span class="al-tool"><span class="al-tool-label">Show</span>' + seg("data-al-kind", KINDS, al.view.kind) + "</span>" +
    "</div>" +
    (chips ? '<div class="al-chips">' + chips + "</div>" : "")
  );
}

function renderToolbar(d) {
  const bar = document.getElementById("activity-toolbar");
  if (!bar) return;
  bar.hidden = !al.full || !d || !d.chats.length;
  if (bar.hidden) return;
  // The search box is kept across renders so typing never loses focus.
  let search = bar.querySelector(".al-search");
  if (!search) {
    bar.innerHTML = '<div class="al-tools"></div><input type="search" class="filter-input al-search" placeholder="Find a chat by name, repo or person" aria-label="Find a chat">';
    search = bar.querySelector(".al-search");
    search.addEventListener("input", () => {
      al.view.q = search.value;
      render();
    });
  }
  search.value = al.view.q;
  bar.querySelector(".al-tools").innerHTML = toolbarHTML(d);
}

function skeletonHTML(w, h) {
  const ph = h - M.t - M.b;
  const wave = (fs) =>
    fs.map((f, i, a) => (i ? "L" : "M") + (M.l + (i / (a.length - 1)) * (w - M.l - M.r)).toFixed(1) + "," + (M.t + ph * f).toFixed(1)).join("");
  return (
    '<div class="al-unit al-skel-text"></div>' +
    '<svg class="al-svg al-skel" width="' + w + '" height="' + h + '" aria-hidden="true">' +
    frameSVG(w, h, { values: [0, 1, 2, 3, 4], top: 4, labels: false }, [], null) +
    '<path class="al-skel-line" pathLength="100" d="' + wave([0.7, 0.45, 0.6, 0.3, 0.5, 0.25, 0.4, 0.35]) + '"/>' +
    '<path class="al-skel-line two" pathLength="100" d="' + wave([0.85, 0.75, 0.55, 0.7, 0.6, 0.8, 0.5, 0.65]) + '"/>' +
    "</svg>" +
    '<div class="al-legend">' +
    [0, 1, 2]
      .map(() => '<div class="al-row al-skel-row"><span class="al-key"></span><span class="al-name"><span class="al-skel-bar"></span><span class="al-skel-bar short"></span></span></div>')
      .join("") +
    "</div>"
  );
}

function messageHTML(w, h, title, detail, retry) {
  return (
    '<div class="al-unit"></div>' +
    '<div class="al-chart-wrap">' +
    '<svg class="al-svg" width="' + w + '" height="' + h + '" aria-hidden="true">' +
    frameSVG(w, h, { values: [0, 1, 2, 3, 4], top: 4, labels: false }, [], null) +
    "</svg>" +
    '<div class="al-msg"><div class="al-msg-title">' + escapeHTML(title) + "</div>" +
    (detail ? '<div class="al-msg-detail">' + escapeHTML(detail) + "</div>" : "") +
    (retry ? '<button type="button" class="btn btn-sm" data-al-retry="1">Try again</button>' : "") +
    "</div></div>"
  );
}

function setBusy(on) {
  const host = document.getElementById("activity-lines");
  if (host) host.classList.toggle("al-busy", on && !!al.data);
}

function render() {
  const host = document.getElementById("activity-lines");
  if (!host) return;
  const panel = document.getElementById("activity-panel");
  if (panel) panel.classList.toggle("al-full", al.full);
  document.body.classList.toggle("al-full-open", al.full);
  document.querySelectorAll("[data-al-range]").forEach((b) => {
    const on = b.dataset.alRange === al.range;
    b.classList.toggle("active", on);
    b.setAttribute("aria-pressed", on ? "true" : "false");
  });
  const exp = document.getElementById("activity-expand");
  if (exp) {
    exp.innerHTML = al.full ? "&times; Close" : "&#x2922; Full screen";
    exp.setAttribute("aria-label", al.full ? "Close full screen" : "Open full screen");
  }
  const count = document.getElementById("activity-count");
  const d = al.data;
  if (d && d.chats.length) renderToolbar(d);
  const split = splitView() && d && d.chats.length;
  const w = Math.max(280, (host.clientWidth || 600) - (split ? LEGEND_W + 24 : 0));
  const h = chartHeight();
  hideTip(true);

  if (!d) {
    renderToolbar(null);
    if (count) count.textContent = "";
    host.innerHTML = al.error
      ? messageHTML(w, h, "Couldn't load chat activity", al.error, true)
      : skeletonHTML(w, h);
    return;
  }
  if (count) count.textContent = d.chats.length ? d.chats.length + (d.chats.length === 1 ? " chat" : " chats") : "";
  if (!d.chats.length) {
    renderToolbar(d);
    host.innerHTML = messageHTML(w, h, "No chat activity in the last " + rangeOf(d.range).label, "Each chat gets a line here as soon as it uses a tool.", false);
    return;
  }
  const lines = series(d, al.full ? al.view : PANEL_VIEW);
  const chart = lines.length
    ? '<div class="al-chart-wrap">' + chartHTML(d, lines, w, h, al.full ? kindOf(al.view.kind) : null) + "</div>"
    : messageHTML(w, h, "Nothing to chart", "Every chat is hidden or filtered out. Show some below or clear a filter.", false);
  const legend = al.full ? fullLegendHTML(d, lines) : legendHTML(lines);
  host.innerHTML = split
    ? '<div class="al-split"><div class="al-split-chart">' + chart + '</div><div class="al-split-legend" style="max-height:' + (h + 20) + 'px">' + legend + "</div></div>"
    : chart + legend;
  if (al.pinned) showCard(al.pinned, null);
}

function findChat(agentID) {
  return al.data ? al.data.chats.find((c) => c.agent_id === agentID) : null;
}

function cardHTML(c) {
  const d = al.data;
  const parts = [
    ["edits", c.edits],
    ["commands", c.commands],
    ["reads", c.reads],
    ["other", c.other],
  ]
    .filter(([, n]) => n > 0)
    .map(([k, n]) => n.toLocaleString() + " " + (n === 1 ? k.replace(/s$/, "") : k));
  let peak = 0;
  let peakAt = -1;
  c.series.forEach((v, i) => {
    if (v > peak) {
      peak = v;
      peakAt = i;
    }
  });
  const rows = [];
  if (c.owner) rows.push(["Who", c.owner]);
  if (c.runtime) rows.push(["Tool", c.runtime]);
  const where = [baseName(c.repo), c.branch && "branch " + c.branch].filter(Boolean).join(" · ");
  if (where) rows.push(["Where", where]);
  rows.push(["Did", parts.join(" · ") || "nothing"]);
  if (peakAt >= 0) rows.push(["Busiest", peak + " actions, " + sliceLabel(d, peakAt)]);
  if (c.last_tool && c.last_seen) {
    const what = c.last_tool + (c.last_file ? " " + relPath(c.last_file, c.repo) : "");
    rows.push(["Last", what + " · " + ago(new Date(c.last_seen * 1000).toISOString())]);
  }
  let html =
    '<div class="al-card-title">' + escapeHTML(chatName(c)) + "</div>" +
    '<div class="al-card-id">' + escapeHTML(c.agent_id) + "</div>" +
    '<dl class="al-card-rows">' +
    rows.map(([k, v]) => "<dt>" + escapeHTML(k) + "</dt><dd>" + escapeHTML(v) + "</dd>").join("") +
    "</dl>";
  const held = c.held_files || [];
  if (held.length) {
    html +=
      '<div class="al-card-h">Holding ' + held.length + (held.length === 1 ? " file" : " files") + "</div><ul>" +
      held.slice(0, 3).map((f) => "<li>" + escapeHTML(relPath(f, c.repo)) + "</li>").join("") +
      (held.length > 3 ? "<li>+" + (held.length - 3) + " more</li>" : "") +
      "</ul>";
  }
  const top = c.top_files || [];
  if (top.length) {
    html +=
      '<div class="al-card-h">Most edited</div><ul>' +
      top.slice(0, 3).map((f) => "<li>" + escapeHTML(relPath(f.path, c.repo)) + " <span>" + f.count + "×</span></li>").join("") +
      "</ul>";
  }
  html += canHover()
    ? '<div class="al-card-foot">' + (al.full ? "Click to show or hide its line · ↗ opens the session" : "Click to open this session") + "</div>"
    : '<button type="button" class="btn btn-sm btn-primary al-open" data-al-open="' + escapeHTML(c.agent_id) + '">Open session</button>';
  return html;
}

// Floating elements live on <body>: page sections animate with a
// transform, which would make a fixed element position against the
// section, not the window.
function floating(id, cls) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("div");
    el.id = id;
    el.className = cls;
    el.hidden = true;
    el.addEventListener("click", (e) => {
      const open = e.target.closest("[data-al-open]");
      if (open) openSession(open.dataset.alOpen);
    });
    document.body.appendChild(el);
  }
  return el;
}

function place(el, anchor, x) {
  const cw = el.offsetWidth;
  const ch = el.offsetHeight;
  let left = x + 16;
  if (left + cw > window.innerWidth - 8) left = Math.max(8, x - 16 - cw);
  let top = anchor.bottom + 6;
  if (top + ch > window.innerHeight - 8) top = Math.max(8, anchor.top - 6 - ch);
  el.style.left = left + "px";
  el.style.top = top + "px";
}

function showCard(agentID, ev) {
  const card = floating("al-card", "al-card");
  const c = findChat(agentID);
  const row = document.querySelector('.al-row[data-al-agent="' + CSS.escape(agentID) + '"]');
  if (!c || !row) return;
  card.innerHTML = cardHTML(c);
  card.hidden = false;
  const sheet = !canHover();
  card.classList.toggle("sheet", sheet);
  if (sheet) {
    card.style.left = card.style.top = card.style.pointerEvents = "";
    return;
  }
  // A hover card never takes clicks: it would cover the rows below it.
  card.style.pointerEvents = "none";
  const rb = row.getBoundingClientRect();
  const column = row.closest(".al-split-legend");
  if (column) {
    // Beside the legend column, level with the row, over the chart.
    const cb = column.getBoundingClientRect();
    card.style.left = Math.max(8, cb.left - card.offsetWidth - 12) + "px";
    card.style.top = Math.min(Math.max(8, rb.top), window.innerHeight - card.offsetHeight - 8) + "px";
    return;
  }
  place(card, rb, ev ? ev.clientX : rb.left + 200);
}

function hideCard() {
  const card = document.getElementById("al-card");
  if (card && !al.pinned) card.hidden = true;
}

function unpin() {
  al.pinned = "";
  document.querySelectorAll(".al-row.pinned").forEach((r) => r.classList.remove("pinned"));
  hideCard();
}

function highlight(k) {
  const wrap = document.querySelector("#activity-lines .al-chart-wrap");
  if (!wrap) return;
  wrap.classList.toggle("al-hl", k !== null);
  wrap.querySelectorAll(".al-series").forEach((p) => p.classList.toggle("on", String(k) === p.dataset.k));
}

function toggleHidden(key) {
  if (al.view.hidden.has(key)) al.view.hidden.delete(key);
  else al.view.hidden.add(key);
  render();
}

// handleViewClick runs the full-screen controls; true when it handled e.
function handleViewClick(e) {
  const open = e.target.closest("[data-al-open]");
  if (open) {
    openSession(open.dataset.alOpen);
    return true;
  }
  const group = e.target.closest("[data-al-group]");
  if (group) {
    al.view.group = group.dataset.alGroup;
    al.view.hidden.clear();
    render();
    return true;
  }
  const kind = e.target.closest("[data-al-kind]");
  if (kind) {
    al.view.kind = kind.dataset.alKind;
    render();
    return true;
  }
  const chip = e.target.closest("[data-al-chip]");
  if (chip) {
    const keep = al.view.only[chip.dataset.alChip];
    if (keep.has(chip.dataset.v)) keep.delete(chip.dataset.v);
    else keep.add(chip.dataset.v);
    render();
    return true;
  }
  const clear = e.target.closest("[data-al-chip-clear]");
  if (clear) {
    al.view.only[clear.dataset.alChipClear].clear();
    render();
    return true;
  }
  const all = e.target.closest("[data-al-all]");
  if (all && al.data) {
    al.view.hidden = all.dataset.alAll === "hide" ? new Set(groups(al.data, al.view).map((g) => g.key)) : new Set();
    render();
    return true;
  }
  const toggle = e.target.closest(".al-toggle");
  if (toggle) {
    toggleHidden(toggle.dataset.alKey);
    return true;
  }
  return false;
}

// tipAt shows the crosshair at slice i and every line's count there.
function tipAt(i, ev) {
  const g = al.geom;
  const d = al.data;
  if (!g || !d || i < 0) return;
  const svg = document.querySelector("#activity-lines .al-svg");
  if (!svg) return;
  const cx = g.x(i);
  const cross = svg.querySelector(".al-cross");
  cross.setAttribute("x1", cx);
  cross.setAttribute("x2", cx);
  cross.classList.add("on");
  svg.querySelector(".al-dots").innerHTML = g.lines
    .map((l) => '<circle class="al-dot" cx="' + cx.toFixed(1) + '" cy="' + g.y(l.values[i]).toFixed(1) + '" r="3.5" style="fill:' + l.color + '"/>')
    .join("");
  const tip = floating("al-tip", "al-tip");
  const rows = g.lines
    .map((l) => ({ l, v: l.values[i] }))
    .sort((a, b) => b.v - a.v)
    .map(
      ({ l, v }) =>
        '<div class="al-tip-row' + (v ? "" : " zero") + '"><span class="al-tip-key" style="background:' + l.color + '"></span>' +
        "<strong>" + v.toLocaleString() + "</strong><span>" + escapeHTML(l.name) + "</span></div>",
    )
    .join("");
  tip.innerHTML = '<div class="al-tip-head">' + escapeHTML(sliceLabel(d, i)) + "</div>" + rows;
  tip.hidden = false;
  const r = svg.getBoundingClientRect();
  place(tip, { top: r.top + 8, bottom: r.top + 8 }, ev ? ev.clientX : r.left + cx);
}

function hideTip(force) {
  if (al.tipPinned && !force) return;
  al.tipPinned = false;
  const tip = document.getElementById("al-tip");
  if (tip) tip.hidden = true;
  const svg = document.querySelector("#activity-lines .al-svg");
  if (svg && svg.querySelector(".al-cross")) {
    svg.querySelector(".al-cross").classList.remove("on");
    svg.querySelector(".al-dots").innerHTML = "";
  }
}

function sliceFromEvent(ev) {
  const g = al.geom;
  const svg = document.querySelector("#activity-lines .al-svg");
  if (!g || !svg || !g.n) return -1;
  const r = svg.getBoundingClientRect();
  const px = ev.clientX - r.left - M.l;
  if (px < 0 || px > g.pw) return -1;
  return Math.min(g.n - 1, Math.max(0, Math.floor((px / g.pw) * g.n)));
}

// lineNear returns the charted chat whose line passes closest to the
// pointer at slice i, within 14px.
function lineNear(i, ev) {
  const g = al.geom;
  const svg = document.querySelector("#activity-lines .al-svg");
  if (!g || !svg) return null;
  const py = ev.clientY - svg.getBoundingClientRect().top;
  let best = null;
  let dist = 14;
  for (const l of g.lines) {
    const dy = Math.abs(g.y(l.values[i]) - py);
    if (l.chat && dy <= dist) {
      dist = dy;
      best = l;
    }
  }
  return best;
}

function openSession(agentID) {
  unpin();
  hideTip(true);
  agentsState.filter = agentID;
  agentsState.showEnded = true;
  setFull(false);
  navigate("agents");
  const input = document.getElementById("agents-filter");
  if (input) input.value = agentID;
}

function setFull(on) {
  al.full = on;
  unpin();
  render();
}

function bind() {
  const panel = document.getElementById("activity-panel");
  if (!panel || panel.dataset.alBound) return;
  panel.dataset.alBound = "1";

  panel.addEventListener("click", (e) => {
    const rangeBtn = e.target.closest("[data-al-range]");
    if (rangeBtn) {
      if (rangeBtn.dataset.alRange === al.range) return;
      al.range = rangeBtn.dataset.alRange;
      saveRange(al.range);
      unpin();
      load();
      return;
    }
    if (e.target.closest("#activity-expand")) {
      setFull(!al.full);
      return;
    }
    if (e.target.closest("[data-al-retry]")) {
      al.error = "";
      al.failures = 0;
      render();
      load();
      return;
    }
    if (handleViewClick(e)) return;
    if (e.target.closest(".al-hit")) {
      const i = sliceFromEvent(e);
      if (canHover()) {
        const l = i >= 0 ? lineNear(i, e) : null;
        if (l && l.id) openSession(l.id);
        return;
      }
      al.tipPinned = true;
      tipAt(i, e);
      return;
    }
    const row = e.target.closest(".al-row[data-al-agent]");
    if (row) {
      if (canHover()) {
        openSession(row.dataset.alAgent);
        return;
      }
      if (al.pinned === row.dataset.alAgent) {
        unpin();
        return;
      }
      al.pinned = row.dataset.alAgent;
      document.querySelectorAll(".al-row").forEach((r) => r.classList.toggle("pinned", r.dataset.alAgent === al.pinned));
      showCard(al.pinned, e);
    }
  });
  // A tap anywhere else closes the card and the readout.
  document.addEventListener("click", (e) => {
    if (e.target.closest(".al-row") || e.target.closest("#al-card") || e.target.closest(".al-hit")) return;
    if (al.pinned) unpin();
    if (al.tipPinned) hideTip(true);
  });

  panel.addEventListener("pointermove", (e) => {
    if (!canHover()) return;
    if (e.target.closest(".al-hit")) {
      hideCard();
      const i = sliceFromEvent(e);
      const l = i >= 0 ? lineNear(i, e) : null;
      highlight(l ? al.geom.lines.indexOf(l) : null);
      e.target.style.cursor = l ? "pointer" : "crosshair";
      tipAt(i, e);
      return;
    }
    hideTip();
    const row = e.target.closest(".al-row[data-k]");
    if (row) {
      highlight(Number(row.dataset.k));
      if (row.dataset.alAgent) showCard(row.dataset.alAgent, e);
      return;
    }
    highlight(null);
    hideCard();
  });
  panel.addEventListener("pointerleave", () => {
    highlight(null);
    hideTip();
    hideCard();
  });
  panel.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const toggle = e.target.closest(".al-toggle");
    const row = e.target.closest(".al-row[data-al-agent]");
    if (toggle) {
      e.preventDefault();
      toggleHidden(toggle.dataset.alKey);
    } else if (row) {
      e.preventDefault();
      openSession(row.dataset.alAgent);
    }
  });
  panel.addEventListener("focusin", (e) => {
    const row = e.target.closest(".al-row[data-al-agent]");
    if (row) {
      highlight(Number(row.dataset.k));
      showCard(row.dataset.alAgent, null);
    }
  });
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (al.pinned) unpin();
    else if (al.tipPinned) hideTip(true);
    else if (al.full) setFull(false);
  });
  let frame = 0;
  let lastW = 0;
  new ResizeObserver(() => {
    const host = document.getElementById("activity-lines");
    if (!host || host.clientWidth === lastW) return;
    lastW = host.clientWidth;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  }).observe(panel);
}

function tick() {
  if (state.activePage !== "overview" || document.hidden) return;
  load();
}

// refreshActivityLines reloads now, e.g. after the Overview's mine/team
// toggle changes which chats count.
export function refreshActivityLines() {
  if (document.getElementById("activity-panel")) load();
}

// setupActivityLines wires the panel once and keeps it fresh while the
// Overview is open.
export function setupActivityLines() {
  if (!document.getElementById("activity-panel")) return;
  bind();
  render();
  load();
  if (!al.timer) al.timer = setInterval(tick, REFRESH_MS);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) tick();
  });
}

export const _test = { al, render, cardHTML, sliceLabel, chatName, series, groups, niceTicks, timeTicks, rangeOf };
